import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import moment from "moment";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { leavePalette, statusMeta, correctionMeta } from "./attendanceCorrectionHelpers";
import { designShell } from "services/constants/designTokens";
import {
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";

// Presentational history table for Attendance Correction. It renders whatever
// already-filtered/paginated rows it's handed against the active saved-view
// columns (`headers`); every row defers its detail to the parent via onRowClick,
// so no create/approval logic lives here.
const headCellSx = {
  ...sharedTableHeadCellSx,
  position: "static",
  top: "auto",
};

const bodyCellSx = {
  ...sharedTableBodyCellSx,
  verticalAlign: "top",
};

// A punch pair: single "--" when both sides are missing, otherwise two lines.
const PunchCell = ({ inVal, outVal }) => {
  if (!inVal && !outVal) {
    return <Typography sx={{ fontSize: 13, color: leavePalette.subText }}>--</Typography>;
  }
  return (
    <Box sx={{ whiteSpace: "nowrap", lineHeight: 1.5 }}>
      <Typography component="div" sx={{ fontSize: 13, color: leavePalette.text }}>
        In: {inVal || "--"}
      </Typography>
      <Typography component="div" sx={{ fontSize: 13, color: leavePalette.text }}>
        Out: {outVal || "--"}
      </Typography>
    </Box>
  );
};

const renderCell = (row, head) => {
  switch (head.key) {
    case "request_id":
      return (
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: leavePalette.text }}>
          {row.request_id}
        </Typography>
      );
    case "date_display": {
      const m = row.date
        ? moment(row.date, ["YYYY-MM-DD", "DD-MM-YYYY"], true)
        : null;
      const valid = m && m.isValid();
      return (
        <Box sx={{ lineHeight: 1.4 }}>
          <Typography component="div" sx={{ fontSize: 13, fontWeight: 500 }}>
            {valid ? m.format("DD-MM-YYYY") : "—"}
          </Typography>
          <Typography component="div" sx={{ fontSize: 12, color: leavePalette.subText }}>
            {valid ? m.format("ddd") : ""}
          </Typography>
        </Box>
      );
    }
    case "correction_label": {
      const meta = correctionMeta(row.correction_type);
      return (
        <Box
          sx={{
            display: "inline-block",
            px: 1.2,
            py: 0.4,
            borderRadius: 5,
            fontSize: 11.5,
            fontWeight: 600,
            color: meta.color,
            bgcolor: `${meta.color}1A`,
            whiteSpace: "nowrap",
          }}
        >
          {meta.label}
        </Box>
      );
    }
    case "original_display":
      return <PunchCell inVal={row.original_in} outVal={row.original_out} />;
    case "corrected_display":
      return <PunchCell inVal={row.corrected_in} outVal={row.corrected_out} />;
    case "reason":
      return (
        <Box
          sx={{
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={row.reason}
        >
          {row.reason || "—"}
        </Box>
      );
    case "status": {
      const color = statusMeta(row.status, row.color_code);
      return (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color }}>
            {row.status || "—"}
          </Typography>
        </Stack>
      );
    }
    case "applied_display":
      return (
        <Typography sx={{ fontSize: 13, color: leavePalette.subText, whiteSpace: "nowrap" }}>
          {row.applied_on ? moment(row.applied_on).format("DD-MM-YYYY, HH:mm") : "—"}
        </Typography>
      );
    default:
      return row[head.key] ?? "—";
  }
};

const AttendanceCorrectionMobileCard = ({ row, headers, onClick }) => {
  const statusHeader = headers.find((head) => head.key === "status");
  const detailHeaders = headers.filter((head) => head.key !== "status");

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        border: `1px solid ${leavePalette.border}`,
        borderRadius: 2,
        backgroundColor: "#fff",
        cursor: "pointer",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: leavePalette.text,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.date_display || row.date || "-"}
          </Typography>
          {statusHeader ? renderCell(row, statusHeader) : null}
        </Stack>
        {detailHeaders.map((head) => (
          <Stack
            key={head.key}
            direction="row"
            spacing={1}
            justifyContent="space-between"
            sx={{ minWidth: 0 }}
          >
            <Typography sx={{ fontSize: 12, color: leavePalette.subText }}>
              {head.name}
            </Typography>
            <Box
              sx={{
                fontSize: 12.5,
                color: leavePalette.text,
                fontWeight: 600,
                maxWidth: "58%",
                textAlign: "right",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {renderCell(row, head)}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export const AttendanceCorrectionTable = ({
  headers = [],
  rows,
  onRowClick,
  loading = false,
}) => {
  const totalCols = headers.length || 1;
  return (
    <>
    <Box sx={{ display: { xs: "block", sm: "none" } }}>
      {rows.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            color: leavePalette.subText,
            border: `1px solid ${leavePalette.border}`,
            borderRadius: 2,
            backgroundColor: "#fff",
          }}
        >
          {loading
            ? "Loading attendance correction records..."
            : "No attendance correction records found."}
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {rows.map((row, idx) => (
            <AttendanceCorrectionMobileCard
              key={row.id ?? idx}
              row={row}
              headers={headers}
              onClick={() => onRowClick && onRowClick(row)}
            />
          ))}
        </Stack>
      )}
    </Box>
    <TableContainer sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
      <Table size="small" sx={{ minWidth: 900, tableLayout: "fixed" }}>
        <TableHead sx={designShell.tableStickyHead}>
          <TableRow sx={designShell.tableStickyHeadRow}>
            {headers.map((head) => (
              <TableCell key={head.key} sx={headCellSx}>
                {head.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            loading ? (
              <TableRow>
                <TableCell
                  colSpan={totalCols}
                  sx={{ ...bodyCellSx, textAlign: "center", py: 4, color: leavePalette.subText }}
                >
                  Loading attendance correction records...
                </TableCell>
              </TableRow>
            ) : (
              <TableEmptyState
                colSpan={totalCols}
                dataLength={rows}
                title="No Attendance Correction Records Found"
                subtitle="There's nothing to show here yet."
              />
            )
          ) : (
            rows.map((row, idx) => (
              <TableRow
                key={row.id ?? idx}
                hover
                sx={{
                  cursor: "pointer",
                  ...designShell.tableRowHover,
                  "&:last-child td": { borderBottom: "none" },
                }}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {headers.map((head) => (
                  <TableCell key={head.key} sx={bodyCellSx}>
                    {renderCell(row, head)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
};
